import { RowData } from "../types";
import { gerarPayloadFromPlanilha } from "./useGeneratePayload";
import { searchAddress, searchClientByDocument, searchPeopleByDocument, searchPlanbyClient } from "./useSearch";

interface AnalyzedResult {
	payloads: { payload: any; originalIndex: number }[];
	erros: RowData[];
}

export const handleAnalyze = async (data: RowData[], debug: boolean): Promise<AnalyzedResult> => {
	const validRows = data.filter((row) => row.status === "validado");
	const payloads: AnalyzedResult["payloads"] = [];
	const rowsComErro: RowData[] = [];

	const planCache = new Map<string, any>();
	const clientCache = new Map<string, any>();

	for (const row of validRows) {
		const rowPeople = await searchPeopleByDocument(row.cpf);
		const rowAddress = await searchAddress(row.cep);

		if (!row.cnpjimobiliaria || String(row.cnpjimobiliaria).trim() === "") {
			rowsComErro.push({
				...row,
				status: "inválido",
				mensagem: "Informe o CNPJ da imobiliária.",
			});
			continue;
		}

		let rowImobiliaria = clientCache.get(row.cnpjimobiliaria);
		if (!rowImobiliaria) {
			rowImobiliaria = await searchClientByDocument(row.cnpjimobiliaria);
			if (!rowImobiliaria) {
				rowsComErro.push({
					...row,
					status: "inválido",
					mensagem: "Imobiliária não encontrada pelo CNPJ fornecido.",
				});
				continue;
			}
			clientCache.set(row.cnpjimobiliaria, rowImobiliaria);
		}

		const imobiliariaId = Array.isArray(rowImobiliaria) ? rowImobiliaria[0]?.id : rowImobiliaria?.id;

		if (!imobiliariaId) {
			rowsComErro.push({
				...row,
				status: "inválido",
				mensagem: "ID da imobiliária não encontrado.",
			});
			continue;
		}

		let rowPlans = planCache.get(row.cnpjimobiliaria);
		if (!rowPlans) {
			rowPlans = await searchPlanbyClient(imobiliariaId);
			planCache.set(row.cnpjimobiliaria, rowPlans);
		}

		const planosRaw = rowPlans?.__client_plan__;
		const planos = Array.isArray(planosRaw) ? planosRaw : [];

		if (debug) {
			console.log("rowPeople", rowPeople);
			console.log("rowAddress", rowAddress);
			console.log("row", row);
			console.log("Planos encontrados:", planos);
			console.log("Imobiliária encontrada:", rowImobiliaria);
		}

		const expectedFeeKey = "residentialFee";
		const taxaEsperada = Number(parseFloat(String(row["taxa"])).toFixed(2));

		if (!expectedFeeKey) {
			rowsComErro.push({
				...row,
				status: "inválido",
				mensagem: "Campo 'taxaTipo' ausente na planilha. Ex: residentialFee, commercialFee...",
			});
			continue;
		}

		const planoValido = planos.some((plano: any) => {
			const taxaPlano = Number(parseFloat(String(plano[expectedFeeKey] ?? "")).toFixed(2));
			return taxaPlano === taxaEsperada;
		});

		if (!planoValido) {
			rowsComErro.push({
				...row,
				status: "inválido",
				mensagem: `Nenhum plano possui a taxa "${expectedFeeKey}" com valor ${taxaEsperada}. Cadastre o plano corretamente antes de continuar.`,
			});
			continue;
		}

		const ibge = rowAddress?.addressIBGE || row["IBGE"];
		if (!ibge) {
			rowsComErro.push({
				...row,
				status: "inválido",
				mensagem: "CEP inválido, preencha o código IBGE manualmente em uma coluna separada.",
			});
			continue;
		}

		// ✅ Gera o payload e associa ao índice da linha
		const payload = gerarPayloadFromPlanilha(row, rowImobiliaria, rowPeople, rowAddress);
		payloads.push({ payload, originalIndex: row.RowNumber });
	}

	if (debug) {
		console.log("Payloads gerados:", payloads);
		console.log("Erros encontrados:", rowsComErro);
	}

	return {
		payloads,
		erros: rowsComErro,
	};
};
